import { type FC, useState } from "react";

import { OptionalNote } from "./OptionalNote";

import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { TextArea } from "mds/components/TextArea";
import { TextField } from "mds/components/TextField";
import { storeApi } from "store/index";
import { toastSuccessMessage } from "utils/alerts";

type AddSectionModalProps = {
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  onClose: () => void;
  defaultCourseId?: string | null;
  defaultTermId?: string | null;
};

export const AddSectionModal: FC<AddSectionModalProps> = ({
  eventName,
  eventProperties,
  onClose,
  defaultCourseId,
  defaultTermId,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTermId, setSelectedTermId] = useState<string>(defaultTermId || "");
  const [selectedCourseId, setSelectedCourseId] = useState<string>(defaultCourseId || "");

  const handleSubmit = () => {
    if (!title.trim() || !selectedCourseId) {
      return;
    }

    storeApi.sections
      .create({
        course_id: selectedCourseId,
        description: description.trim(),
        term_id: selectedTermId || undefined,
        title: title.trim(),
      })
      .then(() => {
        toastSuccessMessage(t("home.toasts.section_added"));
        onClose();
      });
  };

  const handleTermChange = (termId: string) => {
    setSelectedTermId(termId);
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourseId(courseId);
  };

  return (
    <div className="mt-8 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("home.courses.select_course_label")}</div>

          <CourseMenu
            defaultToAll={false}
            menuClassName="max-h-344"
            menuType="menu"
            selectedCourseId={selectedCourseId}
            onCourseChange={handleCourseChange}
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("home.terms.select_term_label")}</div>

          <TermMenu
            defaultToAll={false}
            menuClassName="max-h-344"
            menuType="menu"
            selectedTermId={selectedTermId}
            onTermChange={handleTermChange}
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("home.section_title_label")}</div>

          <TextField
            eventName={eventName}
            eventProperties={eventProperties}
            placeholder={t("home.placeholder.section_title")}
            value={title}
            isEditing
            onChange={(e) => setTitle(String(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && title.trim() && selectedCourseId && selectedTermId) {
                handleSubmit();
              }
            }}
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1" htmlFor="section-description">
          <div className="flex items-center justify-between gap-1 font-medium">
            {t("glossary.section_description")}
            <OptionalNote />
          </div>

          <TextArea
            eventName={eventName}
            eventProperties={eventProperties}
            id="section-description"
            minRows={3}
            placeholder={t("home.placeholder.section_description")}
            value={description}
            onChange={(e) => setDescription(String(e.target.value))}
          />
        </label>
      </div>

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="s" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button
          disabled={!title.trim() || !selectedCourseId || !selectedTermId}
          kind="blue"
          size="s"
          title={t("common.add.section")}
          onClick={handleSubmit}
        >
          {t("common.add.section")}
        </Button>
      </div>
    </div>
  );
};
